import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Primitives";
import { fmtMoney, fmtDate } from "@/lib/format";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useWonFlow, WON_STAGE } from "@/components/advertising/WonMatch";
import { GripVertical, User, MapPin, CalendarClock, Clock, Flame } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const daysInStage = (l) => {
  const j = l.stage_journey;
  const d = (j && j.length ? j[j.length - 1].entered_at : null) || l.updated_at || l.created_at;
  if (!d) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000));
};
const TEMP = { Hot: "#FE3967", Warm: "#F59E0B", Cold: "#38BDF8" };

export default function Pipeline() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const isManager = can("tenant_owner", "platform_owner", "sales_manager");
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [drag, setDrag] = useState(null);
  const [opts, setOpts] = useState({ users: [], territories: [] });
  const [f, setF] = useState({ salesperson: "", territory: "", temperature: "" });
  const [runWon, wonDialog] = useWonFlow();

  const load = useCallback(async () => {
    const [st, ls] = await Promise.all([api.get("/lead_stages"), api.get("/leads")]);
    setStages(st.data.items.sort((a, b) => a.order - b.order));
    setLeads(ls.data.items);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isManager) return;
    Promise.all([api.get("/users"), api.get("/territories")])
      .then(([u, t]) => setOpts({ users: u.data.items || [], territories: t.data.items || [] }));
  }, [isManager]);

  const doMove = async (lead, stage, extra = {}) => {
    setLeads((p) => p.map((l) => (l.id === lead.id ? { ...l, pipeline_stage: stage.name } : l)));
    try {
      const { data } = await api.put(`/leads/${lead.id}`, { pipeline_stage: stage.name, ...extra });
      if (stage.name === WON_STAGE && data.business_id) toast.success("Won — now add the advertiser's market/zone placement", { action: { label: "Add placement", onClick: () => navigate(`/businesses/${data.business_id}?tab=placements`) } });
      else toast.success(`Moved to ${stage.name}`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); load(); }
  };
  const move = (lead, stage) => {
    if (lead.pipeline_stage === stage.name) return;
    if (stage.name === WON_STAGE) return runWon(lead, (extra) => doMove(lead, stage, extra));
    return doMove(lead, stage);
  };

  const shown = useMemo(() => leads.filter((l) => {
    if (f.salesperson && l.assigned_to !== f.salesperson) return false;
    if (f.territory && l.territory !== f.territory) return false;
    if (f.temperature && (l.temperature || "") !== f.temperature) return false;
    return true;
  }), [leads, f]);
  const colLeads = (name) => shown.filter((l) => (l.pipeline_stage || "New Lead") === name);

  return (
    <div data-testid="page-pipeline">
      <PageHeader title="Sales Pipeline" subtitle="Every lead is one record — drag a card to update its stage. Click a card to open the lead." />
      <div className="flex flex-wrap gap-2 mb-4">
        <FSel label="Temperature" value={f.temperature} onChange={(v) => setF({ ...f, temperature: v })} items={["Hot", "Warm", "Cold"].map((x) => [x, x])} testid="pipe-f-temp" />
        {isManager && <FSel label="Salesperson" value={f.salesperson} onChange={(v) => setF({ ...f, salesperson: v })} items={opts.users.map((u) => [u.id, u.name])} testid="pipe-f-sales" />}
        {isManager && <FSel label="Market" value={f.territory} onChange={(v) => setF({ ...f, territory: v })} items={opts.territories.map((t) => [t.name, t.name])} testid="pipe-f-terr" />}
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((stage) => {
          const items = colLeads(stage.name);
          const total = items.reduce((s, l) => s + Number(l.potential_value || 0), 0);
          return (
            <div key={stage.id} onDragOver={(e) => e.preventDefault()} onDrop={() => drag && move(drag, stage)}
              className="w-[292px] shrink-0" data-testid={`kanban-col-${stage.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: stage.color }} />
                  <span className="font-display font-semibold text-sm text-slate-800 truncate">{stage.name}</span>
                  <span className="tnum text-xs text-slate-400 bg-slate-100 rounded-full px-1.5 shrink-0">{items.length}</span>
                </div>
                <span className="text-xs font-semibold text-slate-500 tnum shrink-0">{fmtMoney(total)}</span>
              </div>
              <div className="space-y-2.5 min-h-[140px] rounded-xl bg-slate-100/70 p-2 border-t-2" style={{ borderColor: stage.color }}>
                {items.map((l) => {
                  const dis = daysInStage(l);
                  return (
                    <div key={l.id} draggable onDragStart={() => setDrag(l)} onDragEnd={() => setDrag(null)}
                      onClick={() => navigate(`/leads/${l.id}`)}
                      className="group rounded-xl border border-slate-200 bg-white p-3 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md hover:border-[#00C9E0]/50 transition-all"
                      data-testid={`lead-card-${l.id}`}>
                      <div className="flex items-start justify-between gap-1">
                        <p className="font-semibold text-sm text-slate-800 leading-tight">{l.business_name}</p>
                        <div className="flex items-center gap-1 shrink-0">
                          {l.temperature && <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white" style={{ background: TEMP[l.temperature] || "#94a3b8" }}><Flame className="h-2.5 w-2.5" />{l.temperature}</span>}
                          <GripVertical className="h-4 w-4 text-slate-300 opacity-0 group-hover:opacity-100" />
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[11px] text-slate-500">
                        {l.contact_name && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{l.contact_name}</span>}
                        {(l.territory || l.city) && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{l.territory || l.city}</span>}
                        {l.next_follow_up && <span className="inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" />{fmtDate(l.next_follow_up)}</span>}
                      </div>
                      {isManager && l.assigned_to_name && <p className="mt-1 text-[10px] text-slate-400">Owner: {l.assigned_to_name}</p>}
                      <div className="mt-2.5 flex items-center justify-between border-t border-slate-100 pt-2">
                        <span className="text-sm font-bold text-[#0094A8] tnum">{fmtMoney(l.potential_value)}</span>
                        <span className="inline-flex items-center gap-2 text-[10px] text-slate-400">
                          {l.last_contact && <span>Last {fmtDate(l.last_contact)}</span>}
                          {dis != null && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{dis}d</span>}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {items.length === 0 && <div className="text-center text-xs text-slate-400 py-8">Drop here</div>}
              </div>
            </div>
          );
        })}
      </div>
      {wonDialog}
    </div>
  );
}

function FSel({ label, value, onChange, items, testid }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger className="w-44" data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent><SelectItem value="__all">All {label}s</SelectItem>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}
